import { infiniteQueryOptions, queryOptions } from "@tanstack/react-query";
import { strapiRepository } from "src/strapi/strapiRepository";
import { components } from "src/strapi/schema";
import {
  getReferencesServerFn,
  getHomepageServerFn,
  getNavbarItemsServerFn,
  getServicesServerFn,
  getCategoryBySlugServerFn,
  getPageBySlugServerFn,
  getContactsServerFn,
  getCategoriesWithReferencesServerFn,
  getReferenceBySlugServerFn,
} from "~/strapi/static-server-fn";

export const referencesDefaultSort = [
  `${"priority" satisfies keyof components["schemas"]["Reference"]}:desc`,
  `${"year" satisfies keyof components["schemas"]["Reference"]}:desc`,
];

export const navbarQueryOptions = () =>
  queryOptions({
    queryKey: ["navbar"],
    queryFn: async () => getNavbarItemsServerFn(),
  });

export const homepageQueryOptions = () =>
  queryOptions({
    queryKey: ["homepage"],
    queryFn: async () => getHomepageServerFn(),
  });

export const inProgressReferencesQueryOptions = () =>
  queryOptions({
    queryKey: ["in-progress-references"],
    queryFn: async () =>
      getReferencesServerFn({
        data: {
          filters: {
            [`${"isFinished" satisfies keyof components["schemas"]["Reference"]}`]:
              {
                $eq: false,
              },
          },
          sort: referencesDefaultSort,
        },
      }),
  });

const finishedReferencesFilters = (categorySlug?: string) => {
  const $and: Record<string, any>[] = [
    {
      [`${"isFinished" satisfies keyof components["schemas"]["Reference"]}`]: {
        $eq: true,
      },
    },
  ];

  if (categorySlug) {
    $and.push({
      [`${"categories" satisfies keyof components["schemas"]["Reference"]}`]: {
        [`${"slug" satisfies keyof components["schemas"]["Category"]}`]: {
          $contains: categorySlug,
        },
      },
    });
  }

  return { $and };
};

export const finishedReferencesQueryOptions = (
  params: {
    categorySlug?: string;
    limit?: number;
  } = {},
) =>
  queryOptions({
    queryKey: ["finished-references", params.categorySlug, params.limit],
    queryFn: async () => {
      const filter = {
        filters: finishedReferencesFilters(params.categorySlug),
        sort: referencesDefaultSort,
        ...(params.limit ? { pagination: { limit: params.limit } } : {}),
      };

      return await getReferencesServerFn({ data: filter });
    },
  });

export const REFERENCES_PAGE_SIZE = 10;

export const finishedReferencesInfiniteQueryOptions = (
  params: {
    categorySlug?: string;
  } = {},
) =>
  infiniteQueryOptions({
    queryKey: ["finished-references-infinite", params.categorySlug],
    queryFn: async ({ pageParam }) => {
      const filter = {
        filters: finishedReferencesFilters(params.categorySlug),
        // createdAt breaks priority/year ties so pages never overlap or skip items
        sort: [
          ...referencesDefaultSort,
          `${"createdAt" satisfies keyof components["schemas"]["Reference"]}:desc`,
        ],
        pagination: { page: pageParam, pageSize: REFERENCES_PAGE_SIZE },
      };

      // Page 1 is fetched by the route loader during prerender, so it exists in
      // the static server-fn cache. Later pages are only requested after the
      // user scrolls and are never prerendered — fetch those directly from
      // Strapi, like the admin preview above does.
      const references =
        pageParam === 1
          ? await getReferencesServerFn({ data: filter })
          : await strapiRepository.getReferences(filter);

      return references ?? [];
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length < REFERENCES_PAGE_SIZE ? undefined : allPages.length + 1,
  });

export const referencesOnHomepageQueryOptions = () =>
  queryOptions({
    queryKey: ["references-on-homepage"],
    queryFn: async () =>
      getReferencesServerFn({
        data: {
          filters: {
            [`${"showOnHomepage" satisfies keyof components["schemas"]["Reference"]}`]:
              {
                $eq: true,
              },
          },
          sort: referencesDefaultSort,
        },
      }),
  });

export const referencesBySlugQueryOptions = (
  slug: string,
  status?: "draft" | "published",
) =>
  queryOptions({
    queryKey: ["reference-by-slug", slug, status ?? "default"],
    // `status` is only set by the Strapi admin preview (see strapi/config/admin.ts).
    // Previews must always bypass the static (build-time) server fn cache and
    // fetch fresh data from Strapi, otherwise they show stale prerendered data.
    queryFn: async () =>
      status
        ? strapiRepository.getReferenceBySlug(slug, status)
        : getReferenceBySlugServerFn({ data: { slug } }),
    ...(status ? { staleTime: 0, gcTime: 0 } : {}),
  });

export const categoriesWithReferencesQueryOptions = () =>
  queryOptions({
    queryKey: ["categories-with-references"],
    queryFn: async () => getCategoriesWithReferencesServerFn(),
  });

export const contactsQueryOptions = () =>
  queryOptions({
    queryKey: ["contacts"],
    queryFn: async () => getContactsServerFn(),
  });

export const servicesQueryOptions = () =>
  queryOptions({
    queryKey: ["services"],
    queryFn: async () => await getServicesServerFn(),
  });

export const categoryBySlugQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["category", slug],
    queryFn: async () => getCategoryBySlugServerFn({ data: { slug } }),
  });

export const pageBySlugQueryOptions = (slug: string) =>
  queryOptions({
    queryKey: ["page", slug],
    queryFn: async () => getPageBySlugServerFn({ data: { slug } }),
  });
